import { StoreModelNames } from "../../dali/type/dali.type";
import { signal } from "@angular/core";
import { ReferenceDataStoreModel } from "../../../models/store/reference-data.store.model";
import { StoreModelCollection } from "../model.collection.abstraction";
import {
  ReferenceDataView,
  type ReferenceId,
  toReferenceDataView,
  IReferenceData,
  ReferenceDataSource,
} from "../../../models/view/reference-data/reference-data.view";

import { ForexRateView } from "@bitwarden/web-vault/app/models/view/reference-data/forex-rate.view";

type ForexRateViewParser = (
  key: string,
  view: ForexRateView,
) => { symbol: string; base: string; rate: number };

const defaultForexRateViewParser = (key: string, view: ForexRateView) => {
  const symbol = key;
  const base = view.base;
  const rate = view.symbols[key];
  return { symbol, base, rate };
};

export class ReferenceDataStoreCollection extends StoreModelCollection {
  /** 3 - Store Model and View **/
  readonly type: StoreModelNames = "ReferenceDataStoreModel";
  protected models: Map<string, ReferenceDataStoreModel> = new Map<
    string,
    ReferenceDataStoreModel
  >();

  private _referenceDataViews = signal<ReferenceDataView[]>(null);
  readonly referenceDataViews = this._referenceDataViews.asReadonly();

  triggerChanges(): void {
    const views: ReferenceDataView[] = [];
    this.models.forEach((model) => {
      views.push(this.deepFreeze(new ReferenceDataView(model)));
    });
    this._referenceDataViews.set(views);
  }

  clear(): void {
    this.models.clear();
    this._referenceDataViews.set(null);
  }

  async processNewForexRates(
    forexDataViews: ForexRateView[],
    parser: ForexRateViewParser = defaultForexRateViewParser,
  ): Promise<void> {
    this.log.info("New forex rates fetched");
    const referenceData: ReferenceDataView[] = [];

    /** map on existing rates id **/
    const refDataIdMap = this.referenceDataViews().reduce((acc, i) => {
      acc.set(`${i.date.date}|${i.base}|${i.symbol}`, true);
      return acc;
    }, new Map<ReferenceId, boolean>());

    forexDataViews.map((forexDataView) => {
      for (const key in forexDataView.symbols) {
        const { base, symbol, rate } = parser(key, forexDataView);
        const id: ReferenceId = `${forexDataView.glossDate.date}|${base}|${symbol}`;

        /** Append any new rates **/
        if (!refDataIdMap.has(id)) {
          const data: IReferenceData = {
            date: {
              date: forexDataView.glossDate.date,
              time: forexDataView.glossDate.time,
              timeZone: forexDataView.glossDate.tz,
              reliableTimeSource: false,
            },
            base,
            symbol,
            rate,
            source: ReferenceDataSource.FOREX,
          };
          referenceData.push(toReferenceDataView(data));
        }
      }
    });

    if (referenceData.length > 0) {
      await this.saveToVault(referenceData, true);
    }
  }
}
